import { useState, useEffect, useCallback, useRef } from 'react';
import { useStore } from '../stores/useStore';
import { getCategories, startQuiz, submitQuizAnswer, type QuizQuestion, type AnswerResult } from '../lib/api';
import { createXAPIClient, type XAPIClient, type XAPIActor } from '../lib/xapi';
import { FilterDrawer } from './FilterDrawer';
import { QuestionRenderer } from './QuestionRenderer';
import { InteractiveQuestion } from './interactive/InteractiveQuestion';
import styles from './Quiz.module.css';

interface QuizProps {
  onComplete: () => void;
  initialCategory?: string;
  initialSubcategory?: string;
}

export function Quiz({ onComplete, initialCategory, initialSubcategory }: QuizProps) {
  const player = useStore((s) => s.player);
  const updateSkillProgress = useStore((s) => s.updateSkillProgress);

  // Quiz session state
  const [answerKey, setAnswerKey] = useState<Record<string, string>>({});
  const [explanationKey, setExplanationKey] = useState<Record<string, string | null>>({});
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState<Array<{ correct: boolean; timeMs: number; category: string }>>([]);

  // Category and length selection
  const [categories, setCategories] = useState<Array<{ category: string; count: number }>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory || '');
  const [selectedSubcategory] = useState<string>(initialSubcategory || '');
  const [quizLength, setQuizLength] = useState<number>(5);
  const [autoStarted, setAutoStarted] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState<number>(0);
  const [lastResult, setLastResult] = useState<AnswerResult | null>(null);
  const [imageModalUrl, setImageModalUrl] = useState<string | null>(null);

  // Filter drawer state
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<{
    category?: string;
    subcategory?: string;
    state?: string;
    gradeLevel?: string;
    source?: string;
    difficulty?: string;
  }>({});

  // xAPI tracking
  const xapiClientRef = useRef<XAPIClient | null>(null);
  const xapiActorRef = useRef<XAPIActor | null>(null);
  const quizSessionIdRef = useRef<string>('');
  const quizStartTimeRef = useRef<number>(0);

  // Initialize xAPI client
  useEffect(() => {
    xapiClientRef.current = createXAPIClient();
    if (player) {
      xapiActorRef.current = xapiClientRef.current.createActor(
        player.id,
        player.name,
        player.name.includes('@') ? player.name : undefined
      );
    }
  }, [player]);

  // Load categories on mount
  useEffect(() => {
    getCategories().then(setCategories).catch(console.error);
  }, []);

  // Auto-start quiz if initialCategory is provided
  useEffect(() => {
    if (initialCategory && player && !autoStarted) {
      setAutoStarted(true);
      loadQuestions();
    }
  }, [initialCategory, player]);

  // xAPI: Send quiz completed statement when quiz finishes
  useEffect(() => {
    const isComplete = questions.length > 0 && currentIndex >= questions.length;
    if (isComplete && xapiClientRef.current?.isEnabled() && xapiActorRef.current) {
      const totalTimeMs = Date.now() - quizStartTimeRef.current;
      const passed = (score / questions.length) >= 0.7; // 70% pass threshold
      const category = selectedCategory || filters.category;
      const quizName = category ? `${category} Quiz` : 'Mixed Quiz';

      const statement = xapiClientRef.current.quizCompleted(
        xapiActorRef.current,
        quizSessionIdRef.current,
        quizName,
        score,
        questions.length,
        totalTimeMs,
        passed,
        category
      );
      xapiClientRef.current.sendStatement(statement).catch(console.error);
    }
  }, [currentIndex, questions.length, score, selectedCategory, filters.category]);

  const loadQuestions = async () => {
    if (!player) return;

    setLoading(true);
    try {
      // Use category from filters if set, otherwise use selectedCategory
      const category = filters.category || selectedCategory || undefined;
      const subcategory = filters.subcategory || selectedSubcategory || undefined;

      // Use the new adaptive quiz API with extended filters
      const session = await startQuiz(
        player.id,
        category,
        subcategory,
        quizLength,
        {
          source: filters.source,
          state: filters.state,
          gradeLevel: filters.gradeLevel,
          difficulty: filters.difficulty,
        }
      );

      setQuestions(session.questions);
      setAnswerKey(session.answerKey);
      setExplanationKey(session.explanationKey);
      setCurrentIndex(0);
      setScore(0);
      setAnswers([]);
      setQuestionStartTime(Date.now());

      // xAPI: Send quiz started statement
      quizSessionIdRef.current = crypto.randomUUID();
      quizStartTimeRef.current = Date.now();
      if (xapiClientRef.current?.isEnabled() && xapiActorRef.current) {
        const quizName = category ? `${category} Quiz` : 'Mixed Quiz';
        const statement = xapiClientRef.current.quizStarted(
          xapiActorRef.current,
          quizSessionIdRef.current,
          quizName,
          category
        );
        xapiClientRef.current.sendStatement(statement).catch(console.error);
      }
    } catch (err) {
      console.error('Failed to start quiz:', err);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];
  const isComplete = questions.length > 0 && currentIndex >= questions.length;

  const handleAnswer = useCallback(async (answer: string) => {
    if (showResult || !currentQuestion || !player) return;

    setSelectedAnswer(answer);
    setShowResult(true);

    const timeMs = Date.now() - questionStartTime;

    // Submit answer to backend for skill tracking
    try {
      const result = await submitQuizAnswer(
        player.id,
        currentQuestion.id,
        answer,
        timeMs
      );
      setLastResult(result);

      // Update local skill progress (for immediate UI feedback)
      updateSkillProgress(currentQuestion.category, result.correct);

      // Use result from server for accuracy
      setAnswers((prev) => [...prev, { correct: result.correct, timeMs, category: currentQuestion.category }]);
      if (result.correct) {
        setScore((prev) => prev + 1);
      }

      // xAPI: Send question answered statement
      if (xapiClientRef.current?.isEnabled() && xapiActorRef.current) {
        const correctAns = answerKey[currentQuestion.id];
        const statement = xapiClientRef.current.questionAnswered(
          xapiActorRef.current,
          currentQuestion.id,
          currentQuestion.text,
          answer,
          result.correct,
          timeMs,
          {
            category: currentQuestion.category,
            subcategory: currentQuestion.subcategory || undefined,
            difficulty: currentQuestion.difficulty,
            correctAnswer: correctAns,
            choices: currentQuestion.answers
          }
        );
        xapiClientRef.current.sendStatement(statement).catch(console.error);
      }
    } catch (err) {
      console.error('Failed to submit answer:', err);
      // Fallback: compare locally for multiple choice
      const correctAnswer = answerKey[currentQuestion.id];
      const correct = answer === correctAnswer;
      setAnswers((prev) => [...prev, { correct, timeMs, category: currentQuestion.category }]);
      if (correct) {
        setScore((prev) => prev + 1);
      }
    }
  }, [showResult, currentQuestion, player, questionStartTime, answerKey, updateSkillProgress]);

  const handleNext = () => {
    setSelectedAnswer(null);
    setShowResult(false);
    setLastResult(null);
    setCurrentIndex((prev) => prev + 1);
    setQuestionStartTime(Date.now());
  };

  const handleRestart = () => {
    setQuestions([]);
    setAnswerKey({});
    setExplanationKey({});
    setCurrentIndex(0);
    setScore(0);
    setAnswers([]);
    setSelectedAnswer(null);
    setShowResult(false);
    setLastResult(null);
  };

  // Format source for display
  const formatSource = (source: string | null): string => {
    if (!source) return '';
    if (source.startsWith('wikidata:')) {
      const id = source.replace('wikidata:', '');
      return `Wikidata ${id}`;
    }
    return source;
  };

  // Get difficulty label
  const getDifficultyLabel = (difficulty: number): string => {
    if (difficulty < -0.5) return 'Easy';
    if (difficulty < 0.5) return 'Medium';
    if (difficulty < 1.0) return 'Hard';
    return 'Expert';
  };

  // Get full-size image URL from thumbnail URL
  const getFullSizeImageUrl = (url: string): string => {
    // Met Museum: convert mobile-large to original
    if (url.includes('images.metmuseum.org') && url.includes('mobile-large')) {
      return url.replace('mobile-large', 'original');
    }
    // Wikimedia: already full-size or use original
    return url;
  };

  // Open image in modal
  const openImageModal = (url: string) => {
    setImageModalUrl(getFullSizeImageUrl(url));
  };

  // Count active filters
  const activeFilterCount = Object.keys(filters).length;

  // Quiz selection screen
  if (questions.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.title}>Start a Quiz</h2>
          <p className={styles.subtitle}>
            Welcome, {player?.name}! Questions adapt to your skill level.
          </p>

          <div className={styles.categorySelect}>
            <label className={styles.label}>Category</label>
            <div className={styles.categoryRow}>
              <select
                value={filters.category || selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setFilters(prev => ({ ...prev, category: e.target.value || undefined, subcategory: undefined }));
                }}
                className={styles.select}
              >
                <option value="">All Categories (Mixed)</option>
                {categories.map((cat) => (
                  <option key={cat.category} value={cat.category}>
                    {cat.category} ({cat.count} questions)
                  </option>
                ))}
              </select>
              <button
                className={styles.filterButton}
                onClick={() => setIsFilterDrawerOpen(true)}
                title="Advanced Filters"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                </svg>
                {activeFilterCount > 0 && (
                  <span className={styles.filterBadge}>{activeFilterCount}</span>
                )}
              </button>
            </div>
          </div>

          <div className={styles.categorySelect}>
            <label className={styles.label}>Quiz Length</label>
            <select
              value={quizLength}
              onChange={(e) => setQuizLength(Number(e.target.value))}
              className={styles.select}
            >
              <option value={1}>1 question</option>
              <option value={5}>5 questions</option>
              <option value={10}>10 questions</option>
              <option value={15}>15 questions</option>
              <option value={20}>20 questions</option>
              <option value={25}>25 questions</option>
            </select>
          </div>

          {/* Show active filters summary */}
          {activeFilterCount > 0 && (
            <div className={styles.activeFilters}>
              {filters.state && <span className={styles.filterTag}>State: {filters.state}</span>}
              {filters.gradeLevel && <span className={styles.filterTag}>Grade: {filters.gradeLevel}</span>}
              {filters.source && <span className={styles.filterTag}>Source: {filters.source}</span>}
              {filters.difficulty && <span className={styles.filterTag}>Difficulty: {filters.difficulty}</span>}
            </div>
          )}

          <button
            onClick={loadQuestions}
            className={styles.startButton}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Start Adaptive Quiz'}
          </button>

          <button onClick={onComplete} className={styles.backButton}>
            Back to Dashboard
          </button>
        </div>

        <FilterDrawer
          isOpen={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFiltersChange={setFilters}
        />
      </div>
    );
  }

  // Quiz complete screen
  if (isComplete) {
    const accuracy = (score / questions.length) * 100;
    const totalTime = answers.reduce((sum, a) => sum + a.timeMs, 0) / 1000;

    // Group by category
    const categoryStats: Record<string, { correct: number; total: number }> = {};
    answers.forEach((a) => {
      if (!categoryStats[a.category]) {
        categoryStats[a.category] = { correct: 0, total: 0 };
      }
      categoryStats[a.category].total++;
      if (a.correct) categoryStats[a.category].correct++;
    });

    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.title}>Quiz Complete!</h2>

          <div className={styles.results}>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>Score</span>
              <span className={styles.resultValue}>
                {score} / {questions.length}
              </span>
            </div>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>Accuracy</span>
              <span className={styles.resultValue}>{accuracy.toFixed(0)}%</span>
            </div>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>Time</span>
              <span className={styles.resultValue}>{totalTime.toFixed(1)}s</span>
            </div>
          </div>

          {/* Category breakdown */}
          {Object.keys(categoryStats).length > 1 && (
            <div className={styles.breakdown}>
              <h3>By Category</h3>
              {Object.entries(categoryStats).map(([cat, stats]) => (
                <div
                  key={cat}
                  className={`${styles.breakdownItem} ${
                    stats.correct / stats.total >= 0.5 ? styles.correct : styles.incorrect
                  }`}
                >
                  <span>{cat}</span>
                  <span>{stats.correct}/{stats.total}</span>
                  <span>{((stats.correct / stats.total) * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          )}

          <div className={styles.breakdown}>
            <h3>Question Breakdown</h3>
            {answers.map((answer, idx) => (
              <div
                key={idx}
                className={`${styles.breakdownItem} ${answer.correct ? styles.correct : styles.incorrect}`}
              >
                <span>Q{idx + 1}</span>
                <span>{answer.correct ? '✓' : '✗'}</span>
                <span>{(answer.timeMs / 1000).toFixed(1)}s</span>
              </div>
            ))}
          </div>

          <div className={styles.actions}>
            <button onClick={handleRestart} className={styles.startButton}>
              New Quiz
            </button>
            <button onClick={onComplete} className={styles.backButton}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz in progress
  const correctAnswer = answerKey[currentQuestion.id];

  return (
    <div className={styles.container}>
      <div className={styles.quizCard}>
        <div className={styles.progress}>
          <div className={styles.progressText}>
            Question {currentIndex + 1} of {questions.length}
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{
                width: `${((currentIndex + 1) / questions.length) * 100}%`,
              }}
            />
          </div>
          <div className={styles.score}>Score: {score}</div>
          <button onClick={onComplete} className={styles.endQuizButton}>
            End Quiz
          </button>
        </div>

        <div className={styles.category}>
          {currentQuestion.category}
          {currentQuestion.subcategory && ` • ${currentQuestion.subcategory}`}
          <span style={{ marginLeft: '0.5rem', opacity: 0.7 }}>
            ({getDifficultyLabel(currentQuestion.difficulty)})
          </span>
        </div>

        <h2 className={styles.question}>
          <QuestionRenderer text={currentQuestion.text} />
        </h2>

        {/* Question image */}
        {currentQuestion.imageUrl && (
          <div className={styles.questionImage}>
            <img src={currentQuestion.imageUrl} alt="Question" />
            <button
              className={styles.expandImageButton}
              onClick={() => openImageModal(currentQuestion.imageUrl!)}
              title="View full size"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
            </button>
          </div>
        )}

        {/* Interactive questions (non-multiple choice) */}
        {currentQuestion.questionType && currentQuestion.questionType !== 'multiple_choice' && currentQuestion.questionType !== 'dropdown' ? (
          <div className={styles.interactiveQuestion}>
            <InteractiveQuestion
              questionType={currentQuestion.questionType}
              interactiveConfig={(currentQuestion.interactiveConfig || {}) as Record<string, unknown>}
              correctAnswer={correctAnswer}
              onSubmit={handleAnswer}
              disabled={showResult}
              showResult={showResult}
              isCorrect={lastResult?.correct}
            />
          </div>
        ) : (
          /* Multiple choice answers */
          <div className={styles.answers}>
            {(currentQuestion.answers || []).map((answer, idx) => {
              const isSelected = selectedAnswer === answer;
              const isCorrect = answer === correctAnswer;
              const answerImage = currentQuestion.answerImages?.[idx];
              let className = styles.answerButton;

              if (showResult) {
                if (isCorrect) {
                  className += ` ${styles.correctAnswer}`;
                } else if (isSelected && !isCorrect) {
                  className += ` ${styles.incorrectAnswer}`;
                }
              } else if (isSelected) {
                className += ` ${styles.selected}`;
              }

              // Add class for image answers
              if (answerImage) {
                className += ` ${styles.answerWithImage}`;
              }

              return (
                <button
                  key={idx}
                  onClick={() => handleAnswer(answer)}
                  className={className}
                  disabled={showResult}
                >
                  <span className={styles.answerLetter}>
                    {String.fromCharCode(65 + idx)}
                  </span>
                  {answerImage ? (
                    <img src={answerImage} alt={answer} className={styles.answerImage} />
                  ) : (
                    <span className={styles.answerText}>
                      <QuestionRenderer text={answer} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {showResult && (
          <div className={styles.feedback}>
            {lastResult?.correct ? (
              <p className={styles.correctFeedback}>Correct!</p>
            ) : (
              <p className={styles.incorrectFeedback}>
                {/* Show feedback for interactive questions */}
                {lastResult?.feedback ? (
                  <>{lastResult.feedback}</>
                ) : currentQuestion.questionType === 'chess_puzzle' ? (
                  <>Incorrect. The correct moves were: <code style={{ background: '#f5f5f5', padding: '0.25rem' }}>{correctAnswer}</code></>
                ) : (
                  <>Incorrect. The answer was: <QuestionRenderer text={correctAnswer} /></>
                )}
              </p>
            )}

            {/* Show partial credit for interactive questions */}
            {lastResult?.partialCredit !== undefined && lastResult.partialCredit > 0 && !lastResult.correct && (
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.5rem 0' }}>
                Partial credit: {Math.round(lastResult.partialCredit * 100)}%
              </p>
            )}

            {/* Show explanation if available */}
            {explanationKey[currentQuestion.id] && (
              <p className={styles.explanation}>
                <QuestionRenderer text={explanationKey[currentQuestion.id]!} />
              </p>
            )}

            {/* Show skill update */}
            {lastResult && (
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.5rem 0' }}>
                {currentQuestion.category} skill: {lastResult.previousTheta.toFixed(2)} → {lastResult.newTheta.toFixed(2)}
              </p>
            )}

            {/* Show source */}
            {currentQuestion.source && (
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.5rem 0' }}>
                Source: {formatSource(currentQuestion.source)}
              </p>
            )}

            <button onClick={handleNext} className={styles.nextButton}>
              {currentIndex + 1 >= questions.length
                ? 'See Results'
                : 'Next Question'}
            </button>
          </div>
        )}
      </div>

      {/* Image modal */}
      {imageModalUrl && (
        <div className={styles.imageModal} onClick={() => setImageModalUrl(null)}>
          <div className={styles.imagePopover} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.closeModalButton}
              onClick={() => setImageModalUrl(null)}
              title="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <img
              src={imageModalUrl}
              alt="Full size"
              className={styles.modalImage}
            />
          </div>
        </div>
      )}
    </div>
  );
}
